import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const containerStyle: React.CSSProperties = { width: "100%", height: "600px", position: "relative" };

// Focus on Gurugram area
const center = { lat: 28.4595, lng: 77.0266 };

interface Location {
  lat: number;
  lng: number;
  id: string;
  category: string;
  status: string;
  severity?: number;
  imageUrl?: string;
}

interface HeatMapProps {
  locations: Location[];
  newIssuesCount?: number;
}

type HeatMapType = "severity" | "count";

// Enhanced severity mapping based on category, status, and actual severity score
const getSeverityWeight = (location: Location, newIssuesCount: number = 0): number => {
  if (typeof location.severity === "number") return location.severity;

  const categorySeverity: Record<string, number> = {
    ELECTRIC_HAZARD: 10,
    BURNING_WASTE: 9,
    STAGNANT_WATER: 8,
    CONSTRUCTION_DEBRIS: 7,
    POTHOLE: 6,
    GARBAGE: 5,
    STRAY_CATTLE: 4,
  };

  const statusMultiplier: Record<string, number> = {
    NEW: 1.2,
    IN_REVIEW: 1.0,
    IN_PROGRESS: 0.8,
    RESOLVED: 0.1,
    REJECTED: 0.0,
  };

  const newIssuesMultiplier = Math.min(1 + newIssuesCount / 100, 2.0);
  const base = categorySeverity[location.category] ?? 5;
  const mult = statusMultiplier[location.status] ?? 1.0;
  return base * mult * newIssuesMultiplier;
};

const HeatMap: React.FC<HeatMapProps> = ({ locations, newIssuesCount = 0 }) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const heatmapLayer = useRef<google.maps.visualization.HeatmapLayer | null>(null);

  const [heatMapType, setHeatMapType] = useState<HeatMapType>("severity");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Build heatmap points only after google maps is ready
  const heatmapData = useMemo(() => {
    if (!window.google?.maps) return [] as google.maps.visualization.WeightedLocation[];

    const filtered = (locations ?? []).filter((l) => l.status !== "REJECTED");

    if (filtered.length === 0) {
      // Fallback demo points (only when maps is ready)
      return [
        { location: new google.maps.LatLng(28.4595, 77.0266), weight: 5 },
        { location: new google.maps.LatLng(28.4695, 77.0366), weight: 3 },
        { location: new google.maps.LatLng(28.4495, 77.0166), weight: 7 },
        { location: new google.maps.LatLng(28.4795, 77.0466), weight: 4 },
      ];
    }

    if (heatMapType === "severity") {
      return filtered.map((loc) => ({
        location: new google.maps.LatLng(loc.lat, loc.lng),
        weight: getSeverityWeight(loc, newIssuesCount),
      }));
    }

    // Count-based: bucket nearby points
    const buckets = new Map<string, { lat: number; lng: number; count: number }>();
    for (const loc of filtered) {
      const latKey = Math.round(loc.lat * 1000) / 1000;
      const lngKey = Math.round(loc.lng * 1000) / 1000;
      const key = `${latKey},${lngKey}`;
      const prev = buckets.get(key);
      if (prev) prev.count += 1;
      else buckets.set(key, { lat: latKey, lng: lngKey, count: 1 });
    }
    return Array.from(buckets.values()).map(({ lat, lng, count }) => ({
      location: new google.maps.LatLng(lat, lng),
      weight: count,
    }));
  }, [locations, newIssuesCount, heatMapType]);

  const maxIntensity = useMemo(() => {
    if (!heatmapData.length) return 20;
    const maxWeight = Math.max(...heatmapData.map((d) => d.weight ?? 1));
    return Math.max(maxWeight * 1.2, 20);
  }, [heatmapData]);

  const getGradient = (type: HeatMapType) =>
    type === "severity"
      ? [
          "rgba(34, 197, 94, 0)",
          "rgba(34, 197, 94, 0.3)",
          "rgba(34, 197, 94, 0.6)",
          "rgba(245, 158, 11, 0.8)",
          "rgba(234, 88, 12, 0.9)",
          "rgba(220, 38, 38, 1)",
        ]
      : [
          "rgba(59, 130, 246, 0)",
          "rgba(59, 130, 246, 0.3)",
          "rgba(59, 130, 246, 0.6)",
          "rgba(147, 51, 234, 0.8)",
          "rgba(220, 38, 38, 0.9)",
          "rgba(220, 38, 38, 1)",
        ];

  // Initialize AFTER: (1) container exists, (2) google maps + visualization loaded, (3) we have some data
  const initializeHeatmap = async () => {
    if (isInitialized || mapInstance.current) return;

    setIsLoading(true);
    setError(null);

    try {
      // Wait for Google Maps Visualization library
      let attempts = 0;
      const maxAttempts = 30;
      while (!window.google?.maps?.visualization && attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 500));
        attempts++;
      }
      if (!window.google?.maps?.visualization) {
        throw new Error("Google Maps Visualization library failed to load. Make sure your script includes &libraries=visualization");
      }
      if (!mapRef.current) {
        throw new Error("Map container not available"); // will be avoided because we always render it
      }

      const map = new google.maps.Map(mapRef.current, {
        center,
        zoom: 12,
        mapTypeControl: false,
        fullscreenControl: true,
        streetViewControl: false,
        restriction: {
          latLngBounds: { north: 28.6, south: 28.3, west: 76.8, east: 77.3 },
          strictBounds: false,
        },
      });
      mapInstance.current = map;

      const heatmap = new google.maps.visualization.HeatmapLayer({
        data: heatmapData,
        map,
        radius: 50,
        opacity: 0.8,
        gradient: getGradient(heatMapType),
        maxIntensity,
      });
      heatmapLayer.current = heatmap;

      setIsInitialized(true);
      setIsLoading(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setIsLoading(false);
    }
  };

  // Kick initialization when prerequisites are ready
  useEffect(() => {
    const ready = !!mapRef.current && !!window.google?.maps && heatmapData.length > 0 && !isInitialized;
    if (ready) void initializeHeatmap();
    else if (!!mapRef.current && !!window.google?.maps && !isInitialized && heatmapData.length === 0) {
      // If maps is ready but no data yet, still initialize empty map so it shows up
      (async () => {
        try {
          if (!mapInstance.current) {
            const map = new google.maps.Map(mapRef.current!, { center, zoom: 12, mapTypeControl: false, fullscreenControl: true, streetViewControl: false });
            mapInstance.current = map;
          }
          setIsInitialized(true);
          setIsLoading(false);
        } catch (e) {
          setError(e instanceof Error ? e.message : "Unknown error");
          setIsLoading(false);
        }
      })();
    } else if (!!mapRef.current && !window.google?.maps) {
      // keep spinner while script loads
      setIsLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current, !!window.google?.maps, heatmapData.length, isInitialized]);

  // Update heatmap when data/type/scale changes
  useEffect(() => {
    if (heatmapLayer.current && heatmapData.length > 0) {
      heatmapLayer.current.setData(heatmapData);
      heatmapLayer.current.setOptions({ maxIntensity, gradient: getGradient(heatMapType) });
    }
  }, [heatmapData, maxIntensity, heatMapType]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (heatmapLayer.current) {
        heatmapLayer.current.setMap(null as unknown as google.maps.Map);
        heatmapLayer.current = null;
      }
      mapInstance.current = null;
    };
  }, []);

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">Issue Heatmap Analysis</h3>
        <p className="text-sm text-muted-foreground mb-4">Visual representation of issues across Gurugram. Switch between severity and count views.</p>

        {/* Heatmap Info Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-center text-blue-800">
            <div className="text-blue-600 mr-2">ℹ️</div>
            <div className="text-sm">
              <span className="font-medium">This is the heatmap of all issues reported so far.</span> The heatmap shows the concentration and severity of issues across different areas, helping identify problem hotspots.
            </div>
          </div>
        </div>

        <Tabs value={heatMapType} onValueChange={(v) => setHeatMapType(v as HeatMapType)} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="severity"><h4 className="font-medium mb-2">Severity-Based Heatmap</h4></TabsTrigger>
            <TabsTrigger value="count"><h4 className="font-medium mb-2">Issue Count Heatmap</h4></TabsTrigger>
          </TabsList>
          <TabsContent value="severity" />
          <TabsContent value="count" />
        </Tabs>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-4 text-sm">
          <span className="font-medium">{heatMapType === "severity" ? "Severity Legend:" : "Count Legend:"}</span>
          <div className="flex items-center gap-2"><div className={`w-4 h-4 rounded ${heatMapType === "severity" ? "bg-green-500" : "bg-blue-500"}`} /><span>{heatMapType === "severity" ? "Low" : "Few Issues"}</span></div>
          <div className="flex items-center gap-2"><div className={`w-4 h-4 rounded ${heatMapType === "severity" ? "bg-yellow-500" : "bg-purple-500"}`} /><span>{heatMapType === "severity" ? "Medium" : "Some Issues"}</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-orange-500" /><span>{heatMapType === "severity" ? "High" : "Many Issues"}</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-500" /><span>{heatMapType === "severity" ? "Critical" : "Critical Area"}</span></div>
        </div>
      </div>

      {/* Map container ALWAYS rendered */}
      <div className="rounded-2xl overflow-hidden relative" style={containerStyle}>
        <div ref={mapRef} className="w-full h-full" />

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80 backdrop-blur-sm z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Loading heatmap...</p>
            </div>
          </div>
        )}

        {/* Error overlay (keeps container mounted so Retry works) */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-10">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <p className="text-red-600 font-medium mb-2">Error loading heatmap</p>
              <p className="text-red-500 text-sm mb-4">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  setIsInitialized(false);
                  // re-attempt init
                  setTimeout(() => initializeHeatmap(), 100);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeatMap;
